import { flexRender, Row, Table } from "@tanstack/react-table";
import classNames from "classnames";
import styles from "./table.module.scss";
import cellStyles from "./cell/cell.module.scss";
import React, { Fragment, useRef } from "react";
import { useTable } from "./table-context";
import { ColumnContext } from "./column-context";
import { getVisibleAndSortedCells } from "./utilities/get-visible-sorted-cells";

export const TableBody = <T,>({ table }: { table: Table<T> }) => {
  const {
    rowChildren,
    expandedRowHeight,
    virtualizer,
    isLeftColumnSticky,
    density,
    tableHeight,
    scrollPosition,
    scrollWidth,
    containerWidth,
    containerHeight,
  } = useTable();

  const { rows } = table.getRowModel();
  const columns = table.getVisibleFlatColumns();
  const { columnOrder } = table.getState();

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() -
        (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const firstColumnIsSticky = columns[0].columnDef.meta?.sticky ?? false;

  return (
    <tbody>
      {paddingTop > 0 && (
        <tr>
          <td style={{ height: paddingTop }} />
        </tr>
      )}
      {virtualRows.map((virtualRow, _) => {
        const row = rows[virtualRow.index] as Row<T>;
        const rowClasses = classNames({
          [styles.row]: !row.getIsExpanded(),
          [styles.rowRelaxed]: density === "relaxed",
          [styles.rowCompact]: density === "compact",
          [styles.rowNoBottomBorder]:
            tableHeight >= containerHeight &&
            virtualRow.index === rows.length - 1,
        });

        return (
          <Fragment key={row.id}>
            <tr key={row.id} className={rowClasses}>
              {getVisibleAndSortedCells(row, columnOrder).map(
                (cell, index: number) => {
                  const isStickyByMeta = cell.column.columnDef.meta?.sticky;

                  const isSticky =
                    isStickyByMeta ||
                    (isLeftColumnSticky &&
                      firstColumnIsSticky &&
                      index === 1) ||
                    (isLeftColumnSticky && !firstColumnIsSticky && index === 0);

                  const isStickyLeft =
                    (isStickyByMeta === "left" && !isLeftColumnSticky) ||
                    (isLeftColumnSticky &&
                      firstColumnIsSticky &&
                      index === 1) ||
                    (isLeftColumnSticky && !firstColumnIsSticky && index === 0);
                  const isStickyRight =
                    cell.column.columnDef.meta?.sticky === "right";
                  const isNonInteractive =
                    !cell.column.columnDef.meta?.nonInteractive;
                  const isSticking =
                    isSticky &&
                    (isStickyLeft
                      ? scrollPosition > 0
                      : Math.ceil(scrollWidth) - Math.ceil(scrollPosition) >
                          containerWidth && scrollWidth > containerWidth);

                  const cellClasses = classNames(
                    styles.baseCellPadding,
                    styles.cell,
                    {
                      [styles.inlineExpandedCellBorder]:
                        row.getIsExpanded() && index === 0,
                      [cellStyles.frozen]: isSticky,
                      [cellStyles.frozenLeft]: isSticky && isStickyLeft,
                      [cellStyles.frozenRight]: isSticky && isStickyRight,
                      [cellStyles.isSticking]: isSticking,
                      [styles.expandedRowBg]: row.getIsExpanded(),
                    },
                  );

                  const columnIsPartOfGroup = !!cell.column.parent;
                  const cellWidth = columnIsPartOfGroup
                    ? cell.column.parent!.getSize() /
                      cell.column.parent!.columns.length
                    : cell.column.getSize();

                  return (
                    <ColumnContext.Provider
                      key={cell.id}
                      value={{
                        id: cell.column.id,
                        depth: cell.column.depth,
                      }}
                    >
                      <td
                        tabIndex={0}
                        style={{
                          minWidth: cellWidth,
                          maxWidth: cellWidth,
                          zIndex: isSticky ? 1 + index : 0,
                          left: isSticky
                            ? index === 0
                              ? 0
                              : cell.column.getStart() + 24
                            : undefined,
                        }}
                        className={cellClasses}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    </ColumnContext.Provider>
                  );
                },
              )}
            </tr>

            {row.getIsExpanded() && (
              <tr
                className={`${styles.inlineExpandedRow} ${styles.expandedRowBg}`}
                style={{ height: expandedRowHeight }}
              >
                {/*Add an additional table cell (or two if user defined left column as sticky) so that the expanded row starts in the second column*/}
                <td
                  className={`${styles.inlineExpandedCell} ${cellStyles.frozen} ${cellStyles.frozenLeft}`}
                  style={{
                    zIndex: 1,
                    left: 0,
                  }}
                />

                <td
                  colSpan={row.getVisibleCells().length - 2}
                  className={styles.inlineExpandedCell}
                  style={{
                    left: columns[0].columnDef.size,
                  }}
                >
                  <div tabIndex={0}>
                    {rowChildren?.find((x) => x.id === row.id)?.children}
                  </div>
                </td>

                {/*Add an additional table cell so that the expanded row ends in the second last column*/}
                <td className={`${styles.inlineExpandedCell}`} />
              </tr>
            )}
          </Fragment>
        );
      })}
      {paddingBottom > 0 && (
        <tr>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      )}
    </tbody>
  );
};
